import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fetchBattleDetail,
  updateBattleParticipation,
} from "../../api/battleService";
import { useAuth } from "../../context/AuthContext";

function formatBattleDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function participantName(row) {
  return row?.user?.username || "Joueur";
}

export default function BattleDetail() {
  const { battleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadBattle = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await fetchBattleDetail(battleId, signal);
      setBattle(data.battle);
      setError("");
    } catch (loadError) {
      setError("Impossible de charger cette battle.");
    } finally {
      setLoading(false);
    }
  }, [battleId]);

  useEffect(() => {
    const controller = new AbortController();
    loadBattle(controller.signal);
    return () => controller.abort();
  }, [loadBattle]);

  const grouped = useMemo(() => {
    const rows = Array.isArray(battle?.participants) ? battle.participants : [];
    return {
      participating: rows.filter((row) => row.status === "participating"),
      declined: rows.filter((row) => row.status === "declined"),
    };
  }, [battle]);

  const respond = async (action) => {
    if (!user) {
      navigate("/authentification#connexion");
      return;
    }
    setError("");
    setMessage("");
    try {
      const data = await updateBattleParticipation(battle.id, action);
      setBattle(data.battle);
      setMessage(action === "participate" ? "Participation enregistree." : "Refus enregistre.");
    } catch (responseError) {
      setError(
        responseError?.response?.data?.error ||
          "Impossible de mettre a jour ta participation.",
      );
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-[32px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          Chargement de la battle...
        </div>
      </div>
    );
  }

  if (!battle) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="rounded-[32px] border border-rose-200 bg-rose-50 p-8 text-sm text-rose-700 shadow-sm">
          {error || "Battle introuvable."}
        </div>
      </div>
    );
  }

  const isClosed = battle.status === "finished" || battle.status === "canceled";

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/" className="text-sm font-semibold text-pixy-blue hover:text-pixy-ink">
        ← Retour accueil
      </Link>

      <section className="mt-6 rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm lg:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.32em] text-pixy-blue">
              Battle {battle.category?.name}
            </div>
            <h1 className="mt-3 text-3xl font-black text-pixy-ink sm:text-5xl">
              {battle.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-500">
              {battle.description || "Rendez-vous commun pour jouer plusieurs parties consécutives dans cette catégorie."}
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-sm text-slate-600">
              <span className="rounded-full border border-emerald-100 bg-pixy-mist px-4 py-2 font-semibold text-pixy-blue">
                {formatBattleDate(battle.starts_at)}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 font-semibold">
                {battle.round_count} parties consécutives
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 font-semibold">
                Organisée par {battle.creator?.username}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 font-semibold">
                {battle.sessions_count}/{battle.round_count} sessions jouées
              </span>
            </div>
          </div>
          <div className="flex shrink-0 gap-4 rounded-[24px] border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            <div>
              <div className="text-3xl font-black text-pixy-ink">{battle.participants_count}</div>
              <div className="font-semibold">participants</div>
            </div>
            <div>
              <div className="text-3xl font-black text-pixy-ink">{battle.declined_count}</div>
              <div className="font-semibold">non participants</div>
            </div>
          </div>
        </div>

        {message ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            disabled={isClosed}
            onClick={() => respond("participate")}
            className="rounded-full bg-pixy-blue px-6 py-3 text-sm font-black uppercase tracking-[0.18em] text-white transition hover:bg-pixy-blue/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Je participe
          </button>
          <button
            type="button"
            disabled={isClosed}
            onClick={() => respond("decline")}
            className="rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-black uppercase tracking-[0.18em] text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Je ne participe pas
          </button>
          <Link
            to={`/game-quiz/${battle.category?.slug}`}
            className="rounded-full border border-emerald-100 bg-pixy-mist px-6 py-3 text-center text-sm font-black uppercase tracking-[0.18em] text-pixy-blue transition hover:bg-pixy-mist/70"
          >
            Ouvrir la catégorie
          </Link>
        </div>
      </section>

      <section className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-pixy-ink">Participe ({grouped.participating.length})</h2>
          <div className="mt-4 space-y-3">
            {grouped.participating.length ? grouped.participating.map((row) => (
              <div key={row.id} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="font-semibold text-slate-900">{participantName(row)}</div>
                <div className="text-sm text-slate-500">
                  {row.completed_sessions_count}/{battle.round_count} parties complétées
                  {row.premium_granted_at ? " - bonus de participation attribué" : ""}
                </div>
              </div>
            )) : <div className="text-sm text-slate-400">Aucun participant pour le moment.</div>}
          </div>
        </div>

        <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-pixy-ink">Ne participe pas ({grouped.declined.length})</h2>
          <div className="mt-4 space-y-3">
            {grouped.declined.length ? grouped.declined.map((row) => (
              <div key={row.id} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 font-semibold text-slate-900">
                {participantName(row)}
              </div>
            )) : <div className="text-sm text-slate-400">Aucun refus pour le moment.</div>}
          </div>
        </div>
      </section>
    </div>
  );
}
